/**
 * Certificates and documents library.
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Icon } from "@iconify/react";
import { Box, Tabs, Tab, Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { VText, VButton } from "form";
import { useAsterController } from "context";
import { getFormatDate } from "utils/date";
import SettingsDocumentCertificate from "./settings.documents.certificate";
import { getCourseImageUrl } from "utils/string";
import { BPopOver, BThumb } from "components";
import { CERT_A4_WIDTH_PX } from "utils/certificateTemplate";
import { downloadCertificatePdf } from "utils/certificatePdf";

/**
 * Searchable list of certificates the learner can download as PDF.
 */
const SettingsDocument = () => {
  const { t, i18n } = useTranslation("common");

  const [controller] = useAsterController();
  const { mobile, certificates } = controller;

  const ref = useRef(null);

  const [tab, setTab] = useState(0);
  const [activeCertId, setActiveCertId] = useState(null);
  const [term, setTerm] = useState("");
  const [downloading, setDownloading] = useState(false);

  const activeCert = useMemo(
    () => (certificates.data || []).find((c) => c.id === activeCertId) || null,
    [certificates.data, activeCertId]
  );

  const showList = useMemo(() => {
    const tmp = term.toLowerCase();
    return (certificates.data || []).filter((v) => v.display_name?.toLowerCase()?.includes(tmp));
  }, [certificates.data, term]);

  useEffect(() => mobile && setTab(0), [mobile]);

  const handleSetTabValue = (_, newValue) => setTab(newValue);

  useEffect(() => window.scroll({ top: 0, left: 0, behavior: "smooth" }), []);

  /**
   * Render certificate HTML then capture to PDF (same pipeline as admin).
   *
   * @param {object} cert Certificate row.
   * @returns {Promise<void>}
   */
  const onPDFDownloadClick = async (cert) => {
    if (downloading || !cert?.id) return;
    setDownloading(true);
    setActiveCertId(cert.id);

    try {
      await new Promise((resolve) => setTimeout(resolve, 450));
      if (!ref.current) throw new Error("Certificate root missing");
      const name = cert.display_name || "certificate";
      await downloadCertificatePdf(ref.current, `certificate-${name}.pdf`);
    } catch (e) {
      console.error("Certificate PDF generation failed:", e);
    } finally {
      setDownloading(false);
      setActiveCertId(null);
    }
  };

  return (
    <Box className="relative">
      {/* Keep fully opaque off-screen so html2canvas can capture styles/images. */}
      <Box
        sx={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          zIndex: -1000,
          pointerEvents: "none",
          opacity: 1,
        }}
      >
        <Box ref={ref} style={{ width: `${CERT_A4_WIDTH_PX}px`, background: "#fff" }}>
          {activeCert && (
            <SettingsDocumentCertificate
              courseTitle={activeCert.display_name}
              runStart={getFormatDate(activeCert.start, i18n.language)}
              runEnd={getFormatDate(activeCert.end, i18n.language)}
              duration={activeCert.duration}
              created={getFormatDate(activeCert.created, i18n.language)}
              objective={activeCert.objective || []}
            />
          )}
        </Box>
      </Box>

      <VText className="text-2xl font-bold">{t("Your Documents")}</VText>
      <Box className="pb-6 flex mt-4">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} className="w-full md:flex hidden justify-center md:justify-start">
            <VText className="text-2xl">{certificates.total}</VText>&nbsp;
            <VText color="secondary" className="text-2xl">
              {t("Contents in total")}
            </VText>
          </Grid>
          <Grid item xs={12} md={8} className="flex justify-end flex-wrap items-center">
            <VButton variant="text" color="secondary" className="text-sm normal-case" onClick={() => setTerm("")} disabled={!term}>
              <Icon icon="akar-icons:circle-check" />
              &nbsp;{t("All status")}
            </VButton>
            <BPopOver
              className="ml-2"
              autoClose={false}
              trigger={
                <VButton variant="text" color="secondary" className="text-sm normal-case">
                  <Icon icon="ant-design:filter-filled" />
                  &nbsp;{t("Filter")}
                </VButton>
              }
            >
              <TextField className="w-64" size="small" value={term} onChange={(e) => setTerm(e.target.value)} />
            </BPopOver>
            <Box className="md:flex hidden h-8 ml-2 v-tab-switch">
              <Tabs orientation="horizontal" value={tab} onChange={handleSetTabValue}>
                <Tab icon={<Icon className="mr-0" icon="akar-icons:grid" />} />
                <Tab icon={<Icon className="mr-0" icon="ci:list-checklist" />} />
              </Tabs>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {tab === 0 && (
        <Box>
          <Grid container spacing={3}>
            {showList.map((cert) => (
              <Grid key={cert.id} item xs={12} sm={6} lg={3}>
                <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 h-full flex flex-col">
                  <Box className="flex flex-col w-full">
                    <BThumb src={getCourseImageUrl(cert.course_image_url)} className="w-full aspect-[9/5] rounded-md" />
                  </Box>
                  <Box className="p-4 flex flex-col gap-1 h-full">
                    <VText className="text-xl text-limit-2">{cert.display_name || "-"}</VText>
                    <VText color="secondary" className="flex items-center text-sm">
                      <Icon icon="lucide:clock-9" />
                      &nbsp;{getFormatDate(cert.created, i18n.language)}
                    </VText>

                    <VButton
                      variant="contained"
                      color="primary"
                      className="w-full normal-case mt-auto"
                      loading={downloading && activeCertId === cert.id}
                      disabled={downloading}
                      onClick={() => onPDFDownloadClick(cert)}
                    >
                      {t("Download")}
                    </VButton>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {tab === 1 && (
        <Box>
          <Grid container spacing={3}>
            {showList.map((cert) => (
              <Grid key={cert.id} item xs={12}>
                <Box className="flex flex-row justify-between rounded border border-solid border-gray-200 dark:border-gray-500 pr-4">
                  <Box className="flex flex-row gap-4 items-center">
                    <BThumb src={getCourseImageUrl(cert.course_image_url)} className="aspect-[9/5] rounded-md h-32 w-56 flex-shrink-0" />
                    <Box className="py-4 flex flex-col w-full gap-0.5">
                      <VText className="text-xl text-limit-1">{cert.display_name || "-"}</VText>
                      <VText color="secondary" className="flex items-center text-sm">
                        <Icon icon="lucide:clock-9" />
                        &nbsp;
                        {getFormatDate(cert.created, i18n.language)}
                      </VText>
                    </Box>
                  </Box>
                  <Box className="flex flex-col justify-center w-3/12">
                    <VButton
                      variant="contained"
                      color="primary"
                      className="w-full normal-case"
                      loading={downloading && activeCertId === cert.id}
                      disabled={downloading}
                      onClick={() => onPDFDownloadClick(cert)}
                    >
                      {t("Download")}
                    </VButton>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default SettingsDocument;
